import { createContext, useState, useContext, useCallback, type ReactNode } from 'react';
import { Viewer } from 'cesium';

// Define the shape of a SubSurface Layer
export interface SubSurfaceLayerInfo {
	id: string; // Unique ID (e.g., 'sensors-layer' or 'asset-1234')
	name: string;
	type: 'primitive' | 'datasource';
}

// A layer that belongs to this asset but was switched off by the user. Its tileset
// is not fetched at all until the layer is turned back on.
export interface DeferredLayerInfo {
	id: string; // Matches the tileset's uniqueId (e.g. 'asset-1234' or 'tileset-<uploadId>')
	name: string;
	cesiumId?: number;
	subSurface?: boolean;
}

export type viewerMode = '3D' | 'PANO';

export type HomeViewState =
	| { type: 'fly'; options: any } // For Asset Location (Priority 1)
	| { type: 'zoom'; target: any; offset?: any } // For Tiles/Damages (Priority 2 & 3)
	| null;

interface CesiumContextType {
	viewer: Viewer | null;
	setViewer: (viewer: Viewer | null) => void;
	viewerMode: viewerMode;
	setViewerMode: (mode: viewerMode) => void;
	selectedDamage: any | null;
	setSelectedDamage: (damage: any | null) => void;
	selectedDamageEntity: any | null;
	setSelectedDamageEntity: (entity: any | null) => void;
	tilesLoaded: boolean;
	setTilesLoaded: (loaded: boolean) => void;
	drawMode: DrawMode;
	setDrawMode: (mode: DrawMode) => void;
	activeTool: ActiveTool;
	setActiveTool: (tool: ActiveTool) => void;
	activeDamageContext: any;
	setActiveDamageContext: ({}: any) => void;

	// Underground Props
	undergroundEnabled: boolean;
	setUndergroundEnabled: (enabled: boolean) => void;

	// New: SubSurface Registry
	subSurfaceLayers: SubSurfaceLayerInfo[];
	// Helper to add/remove layers safely
	registerSubSurfaceLayer: (layer: SubSurfaceLayerInfo, action: 'add' | 'remove') => void;

	// Layers that are known but deliberately not loaded yet
	deferredLayers: DeferredLayerInfo[];
	registerDeferredLayer: (layer: DeferredLayerInfo, action: 'add' | 'remove') => void;
	// Ids the user asked to switch on; TilesLoader fetches them and clears the entry
	pendingLayerLoads: string[];
	requestLayerLoad: (id: string) => void;
	resolveLayerLoad: (id: string) => void;
	homeView: HomeViewState;
	setHomeView: (view: HomeViewState) => void;
}

export type DrawMode = 'EDIT' | 'INSERT' | 'NONE' | 'CANCEL';

/**
 * Map interaction the toolbar has claimed. While it is not 'none', map picking for IFC
 * elements is off.
 */
export type ActiveTool = 'none' | 'measure' | 'clipping' | 'upload';

const CesiumContext = createContext<CesiumContextType | null>(null);

export function CesiumContextProvider({ children }: { children: ReactNode }) {
	const [viewer, setViewer] = useState<Viewer | null>(null);
	const [viewerMode, setViewerMode] = useState<viewerMode>('3D');
	const [selectedDamage, setSelectedDamage] = useState<any | null>(null);
	const [selectedDamageEntity, setSelectedDamageEntity] = useState<any | null>(null);
	const [tilesLoaded, setTilesLoaded] = useState<boolean>(false);
	const [drawMode, setDrawMode] = useState<DrawMode>('NONE');
	const [activeTool, setActiveTool] = useState<ActiveTool>('none');
	const [activeDamageContext, setActiveDamageContext] = useState<any>({});

	const [undergroundEnabled, setUndergroundEnabled] = useState(false);
	const [subSurfaceLayers, setSubSurfaceLayers] = useState<SubSurfaceLayerInfo[]>([]);
	const [deferredLayers, setDeferredLayers] = useState<DeferredLayerInfo[]>([]);
	const [pendingLayerLoads, setPendingLayerLoads] = useState<string[]>([]);
	const [homeView, setHomeView] = useState<HomeViewState>(null);

	// Helper to prevent duplicates and simplify usage
	const registerSubSurfaceLayer = useCallback(
		(layer: SubSurfaceLayerInfo, action: 'add' | 'remove') => {
			setSubSurfaceLayers((prev) => {
				if (action === 'remove') {
					return prev.filter((l) => l.id !== layer.id);
				}
				// Add only if not exists
				if (prev.some((l) => l.id === layer.id)) return prev;
				return [...prev, layer];
			});
		},
		[]
	);

	const registerDeferredLayer = useCallback(
		(layer: DeferredLayerInfo, action: 'add' | 'remove') => {
			setDeferredLayers((prev) => {
				if (action === 'remove') {
					return prev.filter((l) => l.id !== layer.id);
				}
				if (prev.some((l) => l.id === layer.id)) return prev;
				return [...prev, layer];
			});
		},
		[]
	);

	const requestLayerLoad = useCallback((id: string) => {
		setPendingLayerLoads((prev) => (prev.includes(id) ? prev : [...prev, id]));
	}, []);

	const resolveLayerLoad = useCallback((id: string) => {
		setPendingLayerLoads((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : prev));
	}, []);

	return (
		<CesiumContext.Provider
			value={{
				viewer,
				setViewer,
				viewerMode,
				setViewerMode,
				selectedDamage,
				setSelectedDamage,
				selectedDamageEntity,
				setSelectedDamageEntity,
				tilesLoaded,
				setTilesLoaded,
				drawMode,
				setDrawMode,
				activeTool,
				setActiveTool,
				activeDamageContext,
				setActiveDamageContext,
				undergroundEnabled,
				setUndergroundEnabled,
				subSurfaceLayers,
				registerSubSurfaceLayer,
				deferredLayers,
				registerDeferredLayer,
				pendingLayerLoads,
				requestLayerLoad,
				resolveLayerLoad,
				homeView,
				setHomeView,
			}}
		>
			{children}
		</CesiumContext.Provider>
	);
}

export const useCesium = () => {
	const context = useContext(CesiumContext);
	if (!context) throw new Error('useCesium must be used within a CesiumContextProvider');
	return context;
};

export default CesiumContext;
